import { useEffect, useState } from "react";
import {
  AddCatalogueModal,
  StyleStep1,
  StyleStep2,
  StyleStep3,
} from "../../pages";
import { MimaToastUtil } from "../../components";
import { FashCreativesController } from "../../controllers";
import constant from "../../utils/constant";
import { handleImageUpload, removeEmptyKeys } from "../../utils/utils";
import { validationSchema } from "./validationSchema";

const AddCatalogueModalContainer = ({ closeModal }) => {
  const [loading, setLoading] = useState(false);
  const [filterQuery, setFilterQuery] = useState("");

  useEffect(() => {
    const fetchCollections = async () => {
      if (filterQuery) {
        setLoading(true);
        await Promise.all([
          FashCreativesController.getCollections(filterQuery),
        ]);
        setLoading(false);
      }
    };
    fetchCollections();
  }, [filterQuery]);

  //New style break

  const [currentStep, setCurrentStep] = useState(1);

  const onSubmit = async (values) => {
    if (currentStep < 3) {
      return setCurrentStep(currentStep + 1);
    }

    if (values.imageUrls?.length < 1) {
      return MimaToastUtil.error({
        message: "Kindly Add atleast 1 image ",
      });
    }

    setLoading(true);

    let payload;

    let urls = [];
    if (values.imageUrls.length > 0) {
      urls = await handleImageUpload(values.imageUrls);
      if (urls.length < 1 || urls === constant.Failed) {
        setLoading(false);
        return MimaToastUtil.error({
          message: "Image upload failed, try again",
        });
      }
    }

    const unifiedCollections = [
      ...values.styleCollections,
      ...values.newCollections,
    ];

    const updatedVariations = await Promise.all(
      values.variations.map(
        async ({
          customColors,
          colorsAvailable,
          international,
          local,
          addCustomColor,
          isDigital,
          documentUrls,
          ...rest
        }) => {
          const customColorsObjects = customColors?.map((color) => ({
            color,
            type: "CUSTOM",
          }));

          let urls = [];

          if (documentUrls?.length > 0) {
            urls = await handleImageUpload(documentUrls);
          }

          return {
            ...rest,
            ...(values.sellAbroad && values.pricingModel !== "BESPOKE"
              ? { international }
              : {}),
            ...(values.pricingModel !== "BESPOKE" ? { local } : {}),
            ...(colorsAvailable?.length > 0 || customColorsObjects?.length > 0
              ? {
                  colorsAvailable: [
                    ...(colorsAvailable || []),
                    ...(customColorsObjects || []),
                  ].filter((item) => item && Object.keys(item).length > 0),
                }
              : {}),
            //isDigital, ----> Temporary removal of isDigital
            documentUrls: urls,
          };
        },
      ),
    );

    const prePayload = {
      ...values,
      variations: updatedVariations,
    };

    let typeUpdatedVariations;

    if (values.type === "PRODUCT") {
      delete prePayload.availableDays;
      delete prePayload.availableWindow;

      typeUpdatedVariations = prePayload.variations.map(
        ({ durationInMinutes, ...rest }) => rest,
      );
    }

    if (values.type === "SERVICE") {
      typeUpdatedVariations = [...prePayload.variations];
    }

    payload = {
      ...prePayload,
      variations: typeUpdatedVariations,
      imageUrls: urls,
      styleCollections: unifiedCollections,
    };

    delete payload.createCollections;
    delete payload.hasVariations;
    delete payload.newCollections;
    delete payload.sellAbroad;
    delete payload.tradingName;
    delete payload.isMigration;

    if (!values.description) {
      delete payload.description;
    }

    removeEmptyKeys(payload);

    const { status, errorMessage } =
      await FashCreativesController.addCatalogue(payload);
    if (status === constant.Success) {
      setLoading(false);
      MimaToastUtil.success({
        message: constant.Success,
      });
      return closeModal();
    }
    setLoading(false);
    return MimaToastUtil.error({
      message: errorMessage,
    });
  };

  const initialValues = {
    //step 1
    name: "",
    isMigration: false,
    designerName: "",
    business: "",
    type: "PRODUCT",
    bestSelling: false,
    topView: false,
    createCollections: false,
    styleCollections: [],
    newCollections: [],
    description: "",
    gender: "",

    //step 2
    pricingModel: "FIXED_PRICE",
    hasVariations: false,
    variations: [
      {
        sizes: [],
        name: "",
        isSoldOut: false,
        accessories: [],
        addCustomColor: false,
        customColors: [],
        colorsAvailable: [
          // {
          //   color: "",
          //   type: "",
          //   code: "",
          // },
        ],
        location: "",
        local: {
          withoutMaterial: 0,
          currencyCode: "NGN",
          onSaleSlashPrice: 0,
          withMaterial: 0,
        },
        durationInMinutes: 0,
        international: {
          currencyCode: "",
          withMaterial: 0,
          onSaleSlashPrice: 0,
        },
        isDigital: false,
        documentUrls: [],
      },
    ],
    sellAbroad: false,

    //step 3
    imageUrls: [],
    availableWindow: {
      startHour: 0,
      endHour: 0,
    },
    availableDays: [],
    daysRequiredForDelivery: {
      early: 1,
      late: 1,
    },
    hashTags: [],
  };

  const renderStep = (
    currentStep,
    values,
    handleChange,
    handleBlur,
    errors,
    touched,
    setFieldValue,
  ) => {
    switch (currentStep) {
      case 1:
        return (
          <StyleStep1
            values={values}
            handleChange={handleChange}
            handleBlur={handleBlur}
            errors={errors}
            touched={touched}
            setFieldValue={setFieldValue}
            setFilterQuery={setFilterQuery}
          />
        );
      case 2:
        return (
          <StyleStep2
            values={values}
            handleChange={handleChange}
            handleBlur={handleBlur}
            errors={errors}
            touched={touched}
            setFieldValue={setFieldValue}
            // setIsDisabled={setIsDisabled}
          />
        );
      case 3:
        return (
          <StyleStep3
            values={values}
            handleChange={handleChange}
            handleBlur={handleBlur}
            errors={errors}
            touched={touched}
            setFieldValue={setFieldValue}
          />
        );
      default:
        return (
          <StyleStep1
            values={values}
            handleChange={handleChange}
            handleBlur={handleBlur}
            errors={errors}
            touched={touched}
            setFieldValue={setFieldValue}
            setFilterQuery={setFilterQuery}
          />
        );
    }
  };

  return (
    <AddCatalogueModal
      closeModal={closeModal}
      loading={loading}
      onSubmit={onSubmit}
      validationSchema={validationSchema(currentStep)}
      currentStep={currentStep}
      setCurrentStep={setCurrentStep}
      initialValues={initialValues}
      renderStep={renderStep}
    />
  );
};

export default AddCatalogueModalContainer;
